<template>
  <div class="bg-white shadow-sm border border-slate-200 overflow-hidden">
    <div v-if="uploadStatus.uploading" class="fixed inset-0 z-[200] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div class="bg-white rounded-xl shadow-2xl w-full max-w-sm p-6 text-center transform transition-all">
        <h3 class="text-lg font-bold text-[#0d4169] mb-4">{{ uploadStatus.text }}</h3>
        <div class="w-full bg-slate-200 rounded-full h-3 mb-2 overflow-hidden shadow-inner">
          <div
            class="bg-blue-600 h-3 rounded-full transition-all duration-300 ease-out"
            :style="{ width: uploadStatus.progress + '%' }"
          ></div>
        </div>
        <div class="text-sm font-semibold text-slate-500">
          {{ uploadStatus.progress }}%
        </div>
      </div>
    </div>

    <div class="px-6 pt-2 pb-1">
      <div class="flex items-baseline gap-2 flex-wrap">
        <h2 class="text-base font-bold text-[#0d4169] text-left">
          {{ resolvedCompanyName }}
        </h2>
        <span class="text-sm font-bold text-slate-600">Cost Category</span>
      </div>
    </div>

    <FinanceToast :show="toast.show" :variant="toast.variant">{{ toast.message }}</FinanceToast>

    <div v-if="isLoadingCategories" class="px-6 py-12 flex justify-center items-center">
      <div class="h-8 w-8 border-2 border-[#0d4169]/30 border-t-transparent rounded-full animate-spin"></div>
    </div>

    <form v-else @submit.prevent="saveCategory" class="px-6 pb-5 pt-2">
      <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-2">
        <div class="flex flex-col gap-1">
          <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">Cost category name</label>
          <input
            v-model="form.name"
            type="text"
            class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm"
            placeholder="Cost Category"
            required
          />
        </div>

        <div class="flex flex-col gap-1">
          <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">Remark</label>
          <input
            v-model="form.remark"
            type="text"
            class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm"
            placeholder="Remark"
          />
        </div>

        <div class="flex items-end justify-start">
          <button
            type="submit"
            :disabled="isSaving"
            class="min-w-[190px] px-5 py-1.5 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all active:scale-95 whitespace-nowrap w-auto disabled:opacity-50 disabled:cursor-not-allowed flex justify-center items-center gap-2"
          >
            <div
              v-if="isSaving"
              class="h-5 w-5 border-2 border-white/30 border-t-transparent rounded-full animate-spin"
            ></div>
            <span v-if="!isSaving">{{ editingId ? 'Update Cost Category' : 'Save Cost Category' }}</span>
          </button>
        </div>

        <div class="flex items-end justify-start sm:justify-end">
          <button
            type="button"
            @click="handleImport"
            :disabled="isSaving || uploadStatus.uploading"
            class="px-3 py-2 bg-slate-100 text-slate-700 text-[0.8rem] font-bold rounded-md transition-all flex items-center gap-1.5 whitespace-nowrap hover:bg-slate-200"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M4 16v1a2 2 0 002 2h12a2 2 0 002-2v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
            Import (XSL)
          </button>
        </div>
      </div>
    </form>

    <div class="px-6 pb-6">
      <div class="w-full flex justify-end mb-2">
        <input
          v-model="searchName"
          type="text"
          class="w-full sm:w-[260px] px-2.5 pr-8 py-1 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm"
          placeholder="Search by Name"
        />
        <svg
          class="w-4 h-4 text-slate-400 -ml-6 mt-[7px] pointer-events-none"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M21 21l-4.35-4.35m1.35-4.65a7 7 0 11-14 0 7 7 0 0114 0z"
          />
        </svg>
      </div>
      <div class="w-full max-lg:overflow-x-auto">
        <table class="w-full max-lg:min-w-[640px] border-collapse bg-white rounded-xl overflow-hidden shadow-sm">
          <thead>
            <tr>
              <th>No.</th>
              <th>Name</th>
              <th>Remark</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, idx) in filteredCategories" :key="row.id" class="hover:bg-slate-100/50 transition-colors border-b">
              <td>{{ idx + 1 }}</td>
              <td>{{ row.name }}</td>
              <td>{{ row.remark || '-' }}</td>
              <td class="actions">
                <button @click="startEdit(row)">Edit</button>
                <label class="toggle-switch" title="Status">
                  <input type="checkbox" :checked="row.enabled" @change="toggleEnabled(row)">
                  <span class="slider"></span>
                </label>
              </td>
            </tr>
            <tr v-if="filteredCategories.length === 0">
              <td colspan="4" style="text-align:center; color:#94a3b8; padding: 32px 15px; font-style: italic;">
                No cost category registered yet.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { useStore } from 'vuex'
import FinanceToast from '../FinanceToast.vue'
import { API_BASE_URL, apiFetch } from '../../../utils/api'

const store = useStore()

const resolvedCompanyName = computed(() => {
  const user = store.getters.currentUser || store.state.user || {}
  return (
    user?.company?.name ||
    user?.companyName ||
    user?.company_name ||
    user?.company?.company_name ||
    'Company Name'
  )
})

const categories = ref([])
const editingId = ref(null)
const isSaving = ref(false)
const isLoadingCategories = ref(false)
const searchName = ref('')

const filteredCategories = computed(() => {
  const query = String(searchName.value || '').trim().toLowerCase()
  if (!query) return categories.value
  return categories.value.filter((row) => String(row?.name || '').toLowerCase().includes(query))
})

const form = reactive({
  name: '',
  remark: ''
})

const toast = reactive({
  show: false,
  message: '',
  variant: 'success'
})
const uploadStatus = reactive({
  uploading: false,
  progress: 0,
  text: 'Uploading file...'
})
let toastTimer = null

function showToast(message, variant = 'success') {
  if (toastTimer) {
    window.clearTimeout(toastTimer)
    toastTimer = null
  }
  toast.message = message
  toast.variant = variant
  toast.show = true
  const duration = variant === 'error' ? 5000 : 3000
  toastTimer = window.setTimeout(() => {
    toast.show = false
    toast.variant = 'success'
    toastTimer = null
  }, duration)
}

async function loadCategoriesFromApi() {
  isLoadingCategories.value = true
  try {
    const response = await apiFetch('/finance/cost-category')
    if (!response.ok) {
      const err = await response.json().catch(() => ({}))
      showToast(err.message || 'Failed to load cost categories.', 'error')
      categories.value = []
      return
    }
    const parsed = await response.json().catch(() => [])
    categories.value = Array.isArray(parsed) ? parsed : []
    
    categories.value.sort((a, b) => a.name.localeCompare(b.name))
  } catch {
    showToast('Failed to load cost categories.', 'error')
    categories.value = []
  } finally {
    isLoadingCategories.value = false
  }
}

function resetForm() {
  form.name = ''
  form.remark = ''
  editingId.value = null
}

async function saveCategory() {
  if (isSaving.value) return
  const name = String(form.name || '').trim()
  const remark = String(form.remark || '').trim()
  if (!name) return
  isSaving.value = true

  try {
    if (editingId.value) {
      const response = await apiFetch(`/finance/cost-category/${editingId.value}`, {
        method: 'PUT',
        body: JSON.stringify({ name, remark })
      })
      if (!response.ok) {
        const err = await response.json().catch(() => ({}))
        showToast(err.message || 'Failed to update cost category.', 'error')
        return
      }
      showToast('Cost category updated successfully.', 'success')
      resetForm()
      await loadCategoriesFromApi()
      return
    }

    const response = await apiFetch('/finance/cost-category', {
      method: 'POST',
      body: JSON.stringify({ name, remark })
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({}))
      showToast(err.message || 'Failed to save cost category.', 'error')
      return
    }
    showToast('Cost category saved successfully.', 'success')
    resetForm()
    await loadCategoriesFromApi()
  } finally {
    isSaving.value = false
  }
}

function startEdit(row) {
  editingId.value = row.id
  form.name = row.name || ''
  form.remark = row.remark || ''
}

async function toggleEnabled(row) {
  const nextEnabled = !row.enabled
  try {
    const response = await apiFetch(`/finance/cost-category/${row.id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ enabled: nextEnabled })
    })
    if (!response.ok) {
      const err = await response.json().catch(() => ({}))
      showToast(err.message || 'Failed to update status.', 'error')
      return
    }
    const idx = categories.value.findIndex((c) => c.id === row.id)
    if (idx >= 0) categories.value[idx] = { ...categories.value[idx], enabled: nextEnabled }
  } catch {
    showToast('Failed to update status.', 'error')
  }
}

function handleImport() {
  const input = document.createElement('input')
  input.type = 'file'
  input.accept = '.xlsx, .csv'
  input.onchange = (e) => {
    const file = e.target.files[0]
    if (!file) return

    const formData = new FormData()
    formData.append('file', file)

    uploadStatus.uploading = true
    uploadStatus.progress = 0
    uploadStatus.text = 'Uploading file...'

    const xhr = new XMLHttpRequest()
    const url = `${API_BASE_URL}/finance/cost-category/import`
    xhr.open('POST', url, true)

    const token = store.state.token || localStorage.getItem('token') || sessionStorage.getItem('token')
    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`)
    }

    xhr.upload.onprogress = (event) => {
      if (!event.lengthComputable) return
      const percentComplete = Math.round((event.loaded / event.total) * 100)
      uploadStatus.progress = percentComplete
      if (percentComplete === 100) {
        uploadStatus.text = 'Processing data...'
      }
    }

    xhr.onload = async () => {
      uploadStatus.uploading = false
      let result = {}
      try {
        result = JSON.parse(xhr.responseText || '{}')
      } catch {
        result = {}
      }

      const importedCount = Array.isArray(result.results) ? result.results.length : 0
      const rejectedCount = Array.isArray(result.errors) ? result.errors.length : 0
      const summaryMessage = `Import completed. Imported: ${importedCount}, Rejected: ${rejectedCount}`

      if (xhr.status >= 200 && xhr.status < 300) {
        showToast(summaryMessage, rejectedCount > 0 ? 'error' : 'success')
        await loadCategoriesFromApi()
      } else {
        showToast(summaryMessage, 'error')
      }
    }

    xhr.onerror = () => {
      uploadStatus.uploading = false
      showToast('Network error during import.', 'error')
    }

    xhr.send(formData)
  }
  input.click()
}

onMounted(() => {
  loadCategoriesFromApi()
})
</script>

<style scoped>
/* match menuListPage.vue table + actions + toggle styles */
table {
  font-family: 'Segoe UI', sans-serif;
  font-size: 0.8rem;
  color: #1e293b;
}

th,
td {
  padding: 6px 8px;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
  font-size: 0.7rem;
}

th {
  background: #f1f5f9;
  font-weight: 600;
}

tbody tr:hover {
  background: #f1f5f9;
}

.actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.actions button {
  background: none;
  border: none;
  color: #2563eb;
  cursor: pointer;
  text-decoration: underline;
  font-size: 0.85rem;
  padding: 0;
  display: flex;
  align-items: center;
}

.toggle-switch {
  position: relative;
  display: inline-block;
  width: 28px;
  height: 15px;
  cursor: pointer;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  inset: 0;
  background: #cbd5e1;
  border-radius: 34px;
  transition: 0.2s;
}

.slider::before {
  content: '';
  position: absolute;
  width: 10px;
  height: 10px;
  left: 2px;
  top: 2.5px;
  background: white;
  border-radius: 50%;
  transition: 0.2s;
}

.toggle-switch input:checked + .slider {
  background: #22c55e;
}

.toggle-switch input:not(:checked) + .slider {
  background: #ef4444;
}

.toggle-switch input:checked + .slider::before {
  transform: translateX(13px);
}
</style>

